import React, { useState, useRef, useEffect } from "react";
import { Box, Button, Heading } from "grommet";
import { createPortal } from "react-dom";
import { toPng } from "html-to-image";

import { RRZDataSet } from "../models";
import RRZChartLegend from "./RiskReductionZoneChartLegend";
import { DOWNLOAD_MAX_PIXEL_RATIO, DOWNLOAD_TIMEOUT, RRZStructureScale } from "../constants";
import DownloadIcon from "../icons/DownloadIcon";
import RRZChartForExport, { RRZChartItems } from "./RiskReductionZoneChartForExport";

interface StateProps {
  readonly rrz: RRZDataSet;
  readonly size: string;
  readonly structureScale: null | RRZStructureScale;
  readonly geographyFullName: string;
}

// Render children into the off-screen div
const PrintPortal = ({ children }: { children: React.ReactNode }) => {
  const mount = document.getElementById("print-area");
  return !!mount ? createPortal(children, mount) : null;
};

const RRZChart = ({ rrz, size, structureScale, geographyFullName }: StateProps) => {
  const [isPreparing, setIsPreparing] = useState(false);

  const exportRef = useRef<HTMLDivElement>(null);

  const chartExportHeader = `Exposure of buildings to wildfire${
    !!geographyFullName && ` in ${geographyFullName}`
  }`;

  useEffect(() => {
    if (!(isPreparing && exportRef.current)) return;
    const captureAndDownload = async () => {
      try {
        const element = exportRef.current;
        if (!element) return;

        const dataUrl = await toPng(element, {
          // Help with caching issues
          cacheBust: true,
          // Use 'pixelRatio' for resolution
          pixelRatio: DOWNLOAD_MAX_PIXEL_RATIO
        });

        const link = document.createElement("a");
        link.download = `${chartExportHeader.replaceAll(" ", "_")}.png`;
        link.href = dataUrl;
        link.click();
      } catch (err) {
        console.error("Download failed", err);
      } finally {
        // Unmount the component
        setIsPreparing(false);
      }
    };

    // Use a timeout to ensure all assets (SVG logo, fonts)
    // have had a moment to load in the browser.
    setTimeout(captureAndDownload, DOWNLOAD_TIMEOUT);
  }, [isPreparing, chartExportHeader]);

  return structureScale ? (
    <Box pad={{ horizontal: "medium", bottom: "medium" }} className="content-box">
      <Heading level={3}>Exposed buildings by zone</Heading>
      <RRZChartItems size={size} rrz={rrz} structureScale={structureScale} />
      {rrz.total_buildings && (
        <RRZChartLegend structureScale={structureScale} totalStructures={rrz.total_buildings} />
      )}
      {size !== "small" && (
        <Box direction="column" align="end">
          <Button
            pad="0px"
            color="highlight-800"
            size="medium"
            onClick={() => setIsPreparing(true)}
            messages={{
              busy: "Preparing chart",
              success: "Chart ready"
            }}
            busy={isPreparing}
            label="Download chart"
            aria-label="Download chart of exposed buildings by zone."
            icon={<DownloadIcon size="14px" color="var(--highlight-700)" aria-hidden />}
            style={{
              justifyContent: isPreparing ? "center" : undefined
            }}
          />
          {isPreparing && (
            <PrintPortal>
              <RRZChartForExport
                ref={exportRef}
                rrz={rrz}
                size={size}
                structureScale={structureScale}
                header={chartExportHeader}
              />
            </PrintPortal>
          )}
        </Box>
      )}
    </Box>
  ) : (
    <></>
  );
};

export default RRZChart;
